import { useDialog } from '@studio/design-system/dialog';
import type { Device } from '@studio/models/device';
import { DetectionMode } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import { useState } from 'react';
import { useAutoTrackingMode } from '../hooks.js';
import { ControlProtectionDialog } from './dialog.jsx';
import type { ControlProtectionDialogProps } from './dialog.jsx';
import { locateProxy } from '#~/agent/index.js';
import { usePrefState } from '#~/preferences/hooks.js';
import { useRoom } from '#~/room/hooks.js';
import { toHexFromId } from '#~/api/utils.js';

/**
 * Gets the auto tracking protection state for a specific camera.
 *
 * @param deviceId - The id of the camera to get the auto tracking protection state for.
 * @returns The auto tracking protection state for the camera.
 */
export const useAutoTrackingControlProtection = (
  deviceId: T_Maybe<Device['id']>
): T_Maybe<{
  isProtected: boolean;
  triggerAlert: (onOverride?: T_Maybe<() => void>) => void;
}> => {
  const agentId = useRoom((µ) => deviceId && µ.devices[deviceId]?.agentId);

  const mode = useAutoTrackingMode(agentId, deviceId);

  const dialog = useDialog<ControlProtectionDialogProps>(ControlProtectionDialog);
  const [isAlerting, setIsAlerting] = useState<boolean>(false);

  const [skipControlProtection] = usePrefState<boolean>(
    'autotracking.skipControlProtection',
    false
  );

  const stopAutoTracking = async (onOverride?: T_Maybe<() => void>): Promise<void> => {
    if (!deviceId) {
      return;
    }

    const proxy = locateProxy(deviceId);

    const workerId = Object.values(
      proxy?.state?.control?.studio?.autoTrackingReservation ?? {}
    ).find((r) => toHexFromId(r?.cameraId) === deviceId)?.workerId;
    const workerIdHex = toHexFromId(workerId) ?? '';
    const autoTrackingState =
      proxy?.state?.control?.studio?.autoTrackingWorker?.[workerIdHex]?.state;

    if (!autoTrackingState) {
      onOverride?.();
      setIsAlerting(false);

      return;
    }

    await proxy?.mutate(
      {
        control: {
          studio: {
            autoTrackingWorker: {
              [workerIdHex]: {
                state: {
                  control: {
                    detectionMode: DetectionMode.DETECTION_MODE_SEARCHING
                  }
                },
                terminatedAt: +new Date()
              }
            }
          }
        }
      },
      {
        control: {
          studio: {
            autoTrackingWorker: {
              [workerIdHex]: {
                state: {
                  control: {
                    selectedSubjectId: new Uint8Array([1]),
                    selectionBbox: { x1: 1, y1: 1, x2: 1, y2: 1, isHuman: true }
                  }
                }
              }
            }
          }
        }
      },
      false
    );

    setTimeout(() => {
      onOverride?.();
    }, 500);

    setIsAlerting(false);
  };

  return {
    isProtected: mode === DetectionMode.DETECTION_MODE_TRACKING,
    triggerAlert: (onOverride?: T_Maybe<() => void>): void => {
      if (isAlerting) {
        return;
      }

      if (!deviceId) {
        onOverride?.();

        return;
      }

      if (skipControlProtection) {
        stopAutoTracking(onOverride).catch(noop);

        return;
      }

      setIsAlerting(true);

      dialog.open({
        onConfirm: (): void => {
          stopAutoTracking(onOverride)
            .catch(noop)
            .finally(() => {
              dialog.close();
            });
        },

        onDismiss: () => {
          setIsAlerting(false);

          dialog.close();
        }
      });
    }
  };
};
